import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    Alert, CircularProgress, Breadcrumbs, Link, Avatar, IconButton,
    Tooltip, InputAdornment, Dialog, DialogTitle, DialogContent,
    DialogActions, Skeleton, Chip
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PreviewIcon from '@mui/icons-material/Preview';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import WorkIcon from '@mui/icons-material/Work';
import InfoIcon from '@mui/icons-material/Info';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

export const PlayersEditPage = () => {
    const { playerId } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();

    // Stati del form
    const [formData, setFormData] = React.useState({
        nomeGiocatore: '',
        teamId: '',
        ruoloId: '',
        foto: '',
        ordinamento: '',
        note: '',
        flVisibile: true,
        flAttivo: true
    });

    // Stati di controllo
    const [loading, setLoading] = React.useState(true);
    const [loadingPlayer, setLoadingPlayer] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [touched, setTouched] = React.useState({});

    // Gestione modifiche
    const [originalData, setOriginalData] = React.useState({});
    const [hasChanges, setHasChanges] = React.useState(false);
    const [showExitDialog, setShowExitDialog] = React.useState(false);
    const [pendingNavigation, setPendingNavigation] = React.useState(null);

    // Dati giocatore originali
    const [playerData, setPlayerData] = React.useState(null);
    const [playerError, setPlayerError] = React.useState(null);

    // Dati per dropdown
    const [teams, setTeams] = React.useState([]);
    const [ruoli, setRuoli] = React.useState([]);
    const [loadingTeams, setLoadingTeams] = React.useState(true);
    const [loadingRuoli, setLoadingRuoli] = React.useState(true);

    // Preview foto
    const [fotoPreview, setFotoPreview] = React.useState('');
    const [fotoError, setFotoError] = React.useState(false);

    // Carica i dettagli del giocatore
    const loadPlayerDetails = async () => {
        try {
            setLoadingPlayer(true);
            setPlayerError(null);
            const jwt = getJwt();

            const response = await axios.get(
                `${host}/v-player`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        playerId: parseInt(playerId),
                        $limit: 1
                    }
                }
            );

            const data = response.data;
            if (data.data && data.data.length > 0) {
                const player = data.data[0];
                setPlayerData(player);

                // Popola il form con i dati esistenti
                const formDataFromPlayer = {
                    nomeGiocatore: player.nomeGiocatore || '',
                    teamId: player.teamId || '',
                    ruoloId: player.ruoloId || '',
                    foto: player.foto || '',
                    ordinamento: player.ordinamento || '',
                    note: player.note || '',
                    flVisibile: player.flVisibile !== undefined ? player.flVisibile : true,
                    flAttivo: player.flAttivo !== undefined ? player.flAttivo : true
                };

                console.log("formDataFromPlayer : ", formDataFromPlayer);

                setFormData(formDataFromPlayer);
                setOriginalData(formDataFromPlayer);

                // Imposta preview foto se presente
                if (player.foto) {
                    setFotoPreview(player.foto);
                }

            } else {
                setPlayerError("Giocatore non trovato");
            }

        } catch (error) {
            console.error("Errore nel recupero dettagli giocatore:", error);
            setPlayerError("Errore nel caricamento dei dettagli del giocatore");
            notify("Errore nel caricamento del giocatore", { type: 'error' });
        } finally {
            setLoadingPlayer(false);
        }
    };

    // Carica teams
    const loadTeams = async () => {
        try {
            setLoadingTeams(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-team`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    $select: ['id', 'nome'],
                    flVisibile: 1,
                    flAttivo: 1,
                    $sort: { nome: 1 }
                }
            });
            setTeams(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento teams:", error);
            notify("Errore nel caricamento squadre", { type: 'error' });
        } finally {
            setLoadingTeams(false);
        }
    };

    // Carica ruoli
    const loadRuoli = async () => {
        try {
            setLoadingRuoli(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-ruolo`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    $select: ['id', 'nome'],
                    $sort: { nome: 1 }
                }
            });
            setRuoli(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento ruoli:", error);
            notify("Errore nel caricamento ruoli", { type: 'error' });
        } finally {
            setLoadingRuoli(false);
        }
    };

    // Validazione campi (identica al create)
    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'nomeGiocatore':
                if (!value.trim()) {
                    newErrors.nomeGiocatore = 'Il nome del giocatore è obbligatorio';
                } else if (value.trim().length < 2) {
                    newErrors.nomeGiocatore = 'Il nome deve essere di almeno 2 caratteri';
                } else if (value.trim().length > 100) {
                    newErrors.nomeGiocatore = 'Il nome non può superare i 100 caratteri';
                } else {
                    delete newErrors.nomeGiocatore;
                }
                break;

            case 'teamId':
                if (!value) {
                    newErrors.teamId = 'La squadra è obbligatoria';
                } else {
                    delete newErrors.teamId;
                }
                break;

            case 'ruoloId':
                if (!value) {
                    newErrors.ruoloId = 'Il ruolo è obbligatorio';
                } else {
                    delete newErrors.ruoloId;
                }
                break;

            case 'foto':
                // Nessuna validazione: campo sempre opzionale
                delete newErrors.foto;
                break;

            case 'ordinamento':
                if (value && (isNaN(value) || value < 0)) {
                    newErrors.ordinamento = 'L\'ordinamento deve essere un numero positivo';
                } else {
                    delete newErrors.ordinamento;
                }
                break;

            case 'note':
                if (value && value.length > 500) {
                    newErrors.note = 'Le note non possono superare i 500 caratteri';
                } else {
                    delete newErrors.note;
                }
                break;

            default:
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Controlla se ci sono modifiche
    const checkHasChanges = (newFormData) => {
        const changed = JSON.stringify(newFormData) !== JSON.stringify(originalData);
        setHasChanges(changed);
        return changed;
    };

    // Gestisce il cambiamento dei campi
    const handleFieldChange = (name, value) => {
        const newFormData = { ...formData, [name]: value };
        setFormData(newFormData);
        setTouched(prev => ({ ...prev, [name]: true }));

        // Validazione in tempo reale
        validateField(name, value);

        // Controllo modifiche
        checkHasChanges(newFormData);

        // Gestione preview foto
        if (name === 'foto') {
            if (value && value.trim()) {
                setFotoPreview(value.trim());
                setFotoError(false);
            } else {
                setFotoPreview('');
                setFotoError(false);
            }
        }
    };

    // Gestisce errore caricamento foto
    const handleFotoError = () => {
        setFotoError(true);
        // Non aggiungere errori a errors.foto, la preview non deve bloccare il salvataggio
    };

    // Gestisce successo caricamento foto
    const handleFotoLoad = () => {
        setFotoError(false);
        if (errors.foto && errors.foto.includes('Impossibile caricare')) {
            const newErrors = { ...errors };
            delete newErrors.foto;
            setErrors(newErrors);
        }
    };

    // Valida tutto il form
    const validateForm = () => {
        const fieldsToValidate = ['nomeGiocatore', 'teamId', 'ruoloId', 'foto', 'ordinamento', 'note'];
        let isValid = true;

        fieldsToValidate.forEach(field => {
            if (!validateField(field, formData[field])) {
                isValid = false;
            }
            setTouched(prev => ({ ...prev, [field]: true }));
        });

        return isValid;
    };

    // Salva le modifiche
    const handleSave = async () => {
        if (!validateForm()) {
            notify("Correggere gli errori nel form", { type: 'error' });
            return;
        }

        if (!hasChanges) {
            notify("Nessuna modifica da salvare", { type: 'info' });
            return;
        }

        try {
            setSaving(true);
            const jwt = getJwt();

            // Prepara i dati per l'invio
            const dataToSend = {
                nome: formData.nomeGiocatore.trim(),
                teamId: parseInt(formData.teamId),
                ruoloId: parseInt(formData.ruoloId),
                foto: formData.foto.trim() || null,
                ordinamento: formData.ordinamento ? parseInt(formData.ordinamento) : null,
                note: formData.note.trim() || null,
                flVisibile: formData.flVisibile,
                flAttivo: formData.flAttivo
            };

            console.log("dataToSend : ", dataToSend);

            await axios.patch(
                `${host}/tbl-player/${playerId}`,
                dataToSend,
                { headers: { 'Authorization': `Bearer ${jwt}` } }
            );

            notify(`Giocatore "${formData.nomeGiocatore}" aggiornato con successo`, { type: 'success' });

            // Aggiorna i dati originali per resettare hasChanges
            setOriginalData({ ...formData });
            setHasChanges(false);

            // Torna alla pagina di dettaglio del giocatore o lista
            navigate(`/giocatori/${playerId}`);

        } catch (error) {
            console.error("Errore nell'aggiornamento giocatore:", error);

            if (error.response?.data?.message) {
                notify(`Errore: ${error.response.data.message}`, { type: 'error' });
            } else if (error.response?.status === 409) {
                notify("Un giocatore con questo nome esiste già in questa squadra", { type: 'error' });
            } else {
                notify("Errore nell'aggiornamento del giocatore", { type: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    // Reset ai valori originali
    const handleReset = () => {
        setFormData({ ...originalData });
        setErrors({});
        setTouched({});
        setHasChanges(false);

        // Reset preview foto
        if (originalData.foto) {
            setFotoPreview(originalData.foto);
            setFotoError(false);
        } else {
            setFotoPreview('');
            setFotoError(false);
        }
    };

    // Gestione navigazione con modifiche non salvate
    const handleNavigation = (path) => {
        if (hasChanges) {
            setPendingNavigation(path);
            setShowExitDialog(true);
        } else {
            navigate(path);
        }
    };

    // Conferma uscita senza salvare
    const handleConfirmExit = () => {
        setShowExitDialog(false);
        if (pendingNavigation) {
            navigate(pendingNavigation);
        }
        setPendingNavigation(null);
    };

    // Annulla uscita
    const handleCancelExit = () => {
        setShowExitDialog(false);
        setPendingNavigation(null);
    };

    // Carica dati al mount
    React.useEffect(() => {
        if (playerId) {
            setLoading(true);
            Promise.all([loadPlayerDetails(), loadTeams(), loadRuoli()]).finally(() => {
                setLoading(false);
            });
        }
    }, [playerId]);

    // Warning per uscita con modifiche non salvate
    React.useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasChanges]);

    // Loading iniziale
    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>

                    {/* Breadcrumb skeleton */}
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="text" width={300} height={24} />
                    </Box>

                    {/* Header skeleton */}
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Skeleton variant="text" width={250} height={40} />
                            <Skeleton variant="text" width={200} height={20} />
                        </Box>
                        <Skeleton variant="rectangular" width={100} height={36} />
                    </Box>

                    {/* Form skeleton */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Skeleton variant="text" width={200} height={32} sx={{ mb: 3 }} />
                                    <Grid container spacing={3}>
                                        {[1, 2, 3, 4, 5, 6].map(i => (
                                            <Grid item xs={12} md={i % 2 === 0 ? 6 : 12} key={i}>
                                                <Skeleton variant="rectangular" height={56} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                    <Skeleton variant="circular" width={80} height={80} sx={{ mx: 'auto', mb: 2 }} />
                                    <Skeleton variant="text" width={150} height={24} />
                                    <Skeleton variant="text" width={100} height={20} />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </motion.div>
            </Box>
        );
    }

    // Error state
    if (playerError) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>

                    {/* Breadcrumb */}
                    <Breadcrumbs sx={{ mb: 3 }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleNavigation('/giocatori')}
                            sx={{ textDecoration: 'none' }}
                        >
                            Giocatori
                        </Link>
                        <Typography variant="body2" color="text.primary">
                            Errore
                        </Typography>
                    </Breadcrumbs>

                    <Alert
                        severity="error"
                        sx={{ mb: 3 }}
                        action={
                            <Button color="inherit" size="small" onClick={loadPlayerDetails}>
                                Riprova
                            </Button>
                        }
                    >
                        {playerError}
                    </Alert>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => handleNavigation('/giocatori')}
                    >
                        Torna alla lista
                    </Button>
                </motion.div>
            </Box>
        );
    }

    const hasErrors = Object.keys(errors).length > 0;
    const canSave = formData.nomeGiocatore.trim() && formData.teamId && formData.ruoloId && !hasErrors && hasChanges;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >

                {/* Breadcrumb */}
                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleNavigation('/giocatori')}
                        sx={{ textDecoration: 'none' }}
                    >
                        Giocatori
                    </Link>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleNavigation(`/giocatori/${playerId}`)}
                        sx={{ textDecoration: 'none' }}
                    >
                        {playerData?.nomeGiocatore || `Giocatore ${playerId}`}
                    </Link>
                    <Typography variant="body2" color="text.primary">
                        Modifica
                    </Typography>
                </Breadcrumbs>

                {/* Header */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            👨‍💼 Modifica Giocatore
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Modifica i dettagli di "{playerData?.nomeGiocatore || 'Giocatore'}"
                            {hasChanges && (
                                <Chip
                                    label="Modifiche non salvate"
                                    size="small"
                                    color="warning"
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadPlayerDetails}
                            disabled={saving}
                        >
                            Ricarica
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => handleNavigation(`/giocatori/${playerId}`)}
                        >
                            Indietro
                        </Button>
                    </Box>
                </Box>

                {/* Alert modifiche */}
                {hasChanges && (
                    <Alert
                        severity="info"
                        sx={{ mb: 3 }}
                        action={
                            <Button color="inherit" size="small" onClick={handleReset}>
                                Annulla modifiche
                            </Button>
                        }
                    >
                        Hai delle modifiche non salvate. Ricordati di salvare prima di uscire.
                    </Alert>
                )}

                {/* Form */}
                <Grid container spacing={3}>

                    {/* Colonna principale - Dati giocatore */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                            <CardContent sx={{ p: 3 }}>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Informazioni Giocatore
                                    </Typography>
                                </Box>

                                <Grid container spacing={3}>

                                    {/* Nome giocatore */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Nome Giocatore *"
                                            value={formData.nomeGiocatore}
                                            onChange={(e) => handleFieldChange('nomeGiocatore', e.target.value)}
                                            error={touched.nomeGiocatore && !!errors.nomeGiocatore}
                                            helperText={touched.nomeGiocatore && errors.nomeGiocatore}
                                            placeholder="Inserisci il nome del giocatore"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PersonIcon />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    {/* Team */}
                                    <Grid item xs={12} md={6}>
                                        <FormControl
                                            fullWidth
                                            error={touched.teamId && !!errors.teamId}
                                        >
                                            <InputLabel>Squadra *</InputLabel>
                                            <Select
                                                value={formData.teamId}
                                                label="Squadra *"
                                                onChange={(e) => handleFieldChange('teamId', e.target.value)}
                                                disabled={loadingTeams}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <GroupsIcon sx={{ color: '#1565c0' }} />
                                                    </InputAdornment>
                                                }
                                            >
                                                {loadingTeams ? (
                                                    <MenuItem disabled>
                                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                                        Caricamento...
                                                    </MenuItem>
                                                ) : (
                                                    teams.map(team => (
                                                        <MenuItem key={team.id} value={team.id}>
                                                            {team.nome || `Team ${team.id}`}
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                            {touched.teamId && errors.teamId && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                    {errors.teamId}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    {/* Ruolo */}
                                    <Grid item xs={12} md={6}>
                                        <FormControl
                                            fullWidth
                                            error={touched.ruoloId && !!errors.ruoloId}
                                        >
                                            <InputLabel>Ruolo *</InputLabel>
                                            <Select
                                                value={formData.ruoloId}
                                                label="Ruolo *"
                                                onChange={(e) => handleFieldChange('ruoloId', e.target.value)}
                                                disabled={loadingRuoli}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <WorkIcon sx={{ color: '#e65100' }} />
                                                    </InputAdornment>
                                                }
                                            >
                                                {loadingRuoli ? (
                                                    <MenuItem disabled>
                                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                                        Caricamento...
                                                    </MenuItem>
                                                ) : (
                                                    ruoli.map(ruolo => (
                                                        <MenuItem key={ruolo.id} value={ruolo.id}>
                                                            {ruolo.nome || `Ruolo ${ruolo.id}`}
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                            {touched.ruoloId && errors.ruoloId && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                    {errors.ruoloId}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    {/* URL Foto */}
                                    <Grid item xs={12}>
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            Alcuni servizi di hosting immagini (es. Google Drive, Dropbox, ecc.) possono <b>bloccare la visualizzazione dell’anteprima nel form</b> per motivi tecnici (CORS o permessi), anche se l’URL è raggiungibile da browser.<br />
                                            Il link viene comunque salvato e sarà visibile dove supportato. Per una preview sempre funzionante, usa link diretti (es. Imgur, server tuo, ecc.).
                                        </Alert>
                                        <TextField
                                            fullWidth
                                            label="URL Foto Profilo"
                                            value={formData.foto}
                                            onChange={(e) => handleFieldChange('foto', e.target.value)}
                                            error={touched.foto && !!errors.foto}
                                            helperText={touched.foto && errors.foto}
                                            placeholder="https://esempio.com/foto.jpg"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PhotoCameraIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: formData.foto && (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Anteprima">
                                                            <IconButton size="small">
                                                                <PreviewIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    {/* Ordinamento */}
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Ordinamento"
                                            type="number"
                                            value={formData.ordinamento}
                                            onChange={(e) => handleFieldChange('ordinamento', e.target.value)}
                                            error={touched.ordinamento && !!errors.ordinamento}
                                            helperText={touched.ordinamento && errors.ordinamento || "Numero per ordinare i giocatori"}
                                            placeholder="0"
                                            inputProps={{ min: 0, step: 1 }}
                                        />
                                    </Grid>

                                    {/* Note */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Note"
                                            multiline
                                            rows={3}
                                            value={formData.note}
                                            onChange={(e) => handleFieldChange('note', e.target.value)}
                                            error={touched.note && !!errors.note}
                                            helperText={touched.note && errors.note || `${formData.note.length}/500 caratteri`}
                                            placeholder="Note aggiuntive sul giocatore..."
                                        />
                                    </Grid>

                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Colonna laterale - Preview e controlli */}
                    <Grid item xs={12} md={4}>

                        {/* Preview giocatore */}
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Anteprima Giocatore
                                </Typography>

                                <Avatar
                                    src={fotoPreview && !fotoError ? `${host}/image-proxy-mw?url=${encodeURIComponent(fotoPreview)}` : ''}
                                    alt={formData.nomeGiocatore}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        margin: '0 auto 16px',
                                        bgcolor: 'secondary.main',
                                        fontSize: '2rem'
                                    }}
                                    onError={handleFotoError}
                                    onLoad={handleFotoLoad}
                                >
                                    {!fotoPreview || fotoError ?
                                        (formData.nomeGiocatore ?
                                            formData.nomeGiocatore.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() :
                                            '👤'
                                        ) :
                                        null
                                    }
                                </Avatar>

                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    {formData.nomeGiocatore || 'Nome Giocatore'}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
                                    {formData.teamId && (
                                        <Typography variant="body2" color="primary">
                                            {teams.find(t => t.id === parseInt(formData.teamId))?.nome || 'Squadra'}
                                        </Typography>
                                    )}
                                    {formData.teamId && formData.ruoloId && (
                                        <Typography variant="body2" color="text.secondary">•</Typography>
                                    )}
                                    {formData.ruoloId && (
                                        <Typography variant="body2" color="primary">
                                            {ruoli.find(r => r.id === parseInt(formData.ruoloId))?.nome || 'Ruolo'}
                                        </Typography>
                                    )}
                                </Box>

                                {formData.note && (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        {formData.note.length > 100 ? `${formData.note.substring(0, 100)}...` : formData.note}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        {/* Impostazioni */}
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Impostazioni
                                </Typography>

                                <Box sx={{ space: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.flVisibile}
                                                onChange={(e) => handleFieldChange('flVisibile', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Giocatore visibile pubblicamente"
                                        sx={{ mb: 1 }}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.flAttivo}
                                                onChange={(e) => handleFieldChange('flAttivo', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Giocatore attivo"
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Errori */}
                        {hasErrors && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Correggere i seguenti errori:
                                </Typography>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    {Object.values(errors).map((error, index) => (
                                        <li key={index}>
                                            <Typography variant="body2">{error}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        {/* Azioni */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={!canSave || saving}
                                sx={{ py: 1.5 }}
                            >
                                {saving ? 'Salvataggio...' : 'Salva Modifiche'}
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<RestoreIcon />}
                                onClick={handleReset}
                                disabled={saving || !hasChanges}
                            >
                                Ripristina Originale
                            </Button>
                        </Box>
                    </Grid>

                </Grid>

                {/* Footer */}
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        * Campi obbligatori
                        {hasChanges && " • Hai modifiche non salvate"}
                    </Typography>
                </Box>

                {/* Dialog conferma uscita */}
                <Dialog
                    open={showExitDialog}
                    onClose={handleCancelExit}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon color="warning" />
                        Modifiche non salvate
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">
                            Hai delle modifiche non salvate. Se esci ora, le modifiche andranno perse.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Vuoi davvero uscire senza salvare?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelExit}>
                            Rimani qui
                        </Button>
                        <Button onClick={handleConfirmExit} color="warning" variant="contained">
                            Esci senza salvare
                        </Button>
                    </DialogActions>
                </Dialog>

            </motion.div>
        </Box>
    );
};